/**
 * Guest WhatsApp OTP modal.
 *
 * Two-step flow:
 *  1. Enter phone (defaults country code from venue config) → POST /api/otp/send
 *  2. Enter 6-digit code → POST /api/otp/verify → returns guest_token
 *
 * The parent gets `(guest_token, phone, name)` via onVerified() so it can:
 *   - Persist token in sessionStorage
 *   - Attach token to song-request submits
 *   - Show a friendly "1 request per session" message after a 429
 */
import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Loader2, ShieldCheck, ArrowLeft, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GuestOtpModal = ({ sessionCode, defaultName = '', defaultPhone = '', defaultCountryCode = '+91', onVerified, onCancel, inline = false }) => {
  // Split a guest-entered phone string into (countryCode, subscriberNumber).
  // GuestRequestPage's info form stores the phone as a single string; the modal
  // needs them separately so the user can correct just the country code if
  // needed.
  const splitInitialPhone = () => {
    const raw = String(defaultPhone || '').trim();
    if (!raw) return { cc: defaultCountryCode || '+91', num: '' };
    if (raw.startsWith('+')) {
      // Heuristic: 1- or 2-digit CC (covers US +1 / India +91 / most cases)
      const digits = raw.slice(1).replace(/\D/g, '');
      if (digits.length > 10) {
        return { cc: `+${digits.slice(0, digits.length - 10)}`, num: digits.slice(-10) };
      }
      return { cc: defaultCountryCode || '+91', num: digits };
    }
    return { cc: defaultCountryCode || '+91', num: raw.replace(/\D/g, '') };
  };
  const initialSplit = splitInitialPhone();

  const [step, setStep] = useState('phone'); // 'phone' | 'code'
  const [name, setName] = useState(defaultName);
  const [countryCode, setCountryCode] = useState(initialSplit.cc);
  const [phone, setPhone] = useState(initialSplit.num);
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const otpRefs = useRef([]);

  // Countdown for the resend button so guests can't hammer Interakt
  useEffect(() => {
    if (resendCooldown <= 0) return undefined;
    const t = setTimeout(() => setResendCooldown((c) => Math.max(0, c - 1)), 1000);
    return () => clearTimeout(t);
  }, [resendCooldown]);

  const fullPhone = `${countryCode}${phone.replace(/^0+/, '')}`;

  const handleSendOtp = async (e) => {
    e?.preventDefault?.();
    if (!name.trim()) { toast.error('Please enter your name'); return; }
    if (!phone.trim() || phone.replace(/\D/g, '').length < 7) {
      toast.error('Please enter a valid phone number'); return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/otp/send`, {
        code: sessionCode,
        phone: fullPhone,
        country_code: countryCode,
        name: name.trim(),
      });
      toast.success('Code sent via SMS');
      setStep('code');
      setResendCooldown(60);
      // Dev-mode: backend echoes the OTP when Interakt is misconfigured
      if (res.data?.dev_otp) {
        console.log('[DEV] OTP:', res.data.dev_otp);
      }
      setTimeout(() => otpRefs.current[0]?.focus(), 100);
    } catch (err) {
      const detail = err.response?.data?.detail || 'Could not send code';
      toast.error(detail);
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (codeStr = null) => {
    const code = codeStr || otp.join('');
    if (code.length !== 6) { toast.error('Enter the 6-digit code'); return; }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/otp/verify`, {
        code: sessionCode,
        phone: fullPhone,
        otp: code,
      });
      toast.success('Verified — you\'re in!');
      onVerified({
        guest_token: res.data.guest_token,
        phone: fullPhone,
        name: name.trim(),
      });
    } catch (err) {
      const detail = err.response?.data?.detail || 'Verification failed';
      toast.error(detail);
      // Clear and refocus first cell on error so user can retype
      setOtp(['', '', '', '', '', '']);
      setTimeout(() => otpRefs.current[0]?.focus(), 50);
    } finally {
      setLoading(false);
    }
  };

  const handleOtpChange = (i, val) => {
    const digit = val.replace(/\D/g, '').slice(-1);
    const next = [...otp];
    next[i] = digit;
    setOtp(next);
    if (digit && i < 5) otpRefs.current[i + 1]?.focus();
    // Auto-submit when complete
    if (digit && i === 5 && next.every((d) => d)) {
      setTimeout(() => handleVerify(next.join('')), 80);
    }
  };

  const handleOtpKeyDown = (i, e) => {
    if (e.key === 'Backspace' && !otp[i] && i > 0) {
      otpRefs.current[i - 1]?.focus();
    } else if (e.key === 'ArrowLeft' && i > 0) {
      otpRefs.current[i - 1]?.focus();
    } else if (e.key === 'ArrowRight' && i < 5) {
      otpRefs.current[i + 1]?.focus();
    }
  };

  const handleOtpPaste = (e) => {
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted.length === 6) {
      e.preventDefault();
      setOtp(pasted.split(''));
      setTimeout(() => handleVerify(pasted), 80);
    }
  };

  const body = (
    <>
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2">
            {step === 'code' ? (
              <button
                onClick={() => setStep('phone')}
                className="text-white/60 hover:text-white p-1 -ml-2"
                data-testid="otp-back-btn"
                aria-label="Back"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
            ) : (!inline && onCancel ? (
              <button
                onClick={onCancel}
                className="text-white/60 hover:text-white p-1 -ml-2"
                data-testid="otp-back-btn"
                aria-label="Back"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
            ) : null)}
            <span className="text-[10px] uppercase tracking-[0.4em] text-white/50 font-['JetBrains_Mono']">
              {step === 'phone' ? 'Verify Phone' : 'Enter Code'}
            </span>
          </div>
          {!inline && onCancel && (
            <button
              onClick={onCancel}
              className="text-white/40 hover:text-white text-xs uppercase tracking-widest"
              data-testid="otp-close-btn"
            >
              Close
            </button>
          )}
        </div>

        <AnimatePresence mode="wait">
          {step === 'phone' && (
            <motion.form
              key="phone-step"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              onSubmit={handleSendOtp}
              className="space-y-4"
            >
              <div className="flex items-center gap-3 mb-2">
                <div className="w-12 h-12 rounded-2xl bg-[#FF007F]/15 flex items-center justify-center ring-1 ring-[#FF007F]/30">
                  <MessageSquare className="w-6 h-6 text-[#FF007F]" />
                </div>
                <div>
                  <h2 className="text-xl text-white font-['Outfit'] font-bold leading-tight">
                    Quick SMS check
                  </h2>
                  <p className="text-xs text-white/55">
                    One code, one song. No spam.
                  </p>
                </div>
              </div>

              <div>
                <Label className="text-[9px] uppercase tracking-[0.35em] text-white/50">Name</Label>
                <Input
                  data-testid="otp-name-input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Your name"
                  className="bg-white/[0.03] border-white/15 text-white h-12 rounded-xl mt-1"
                />
              </div>

              <div>
                <Label className="text-[9px] uppercase tracking-[0.35em] text-white/50">Mobile Number</Label>
                <div className="flex gap-2 mt-1">
                  <Input
                    data-testid="otp-cc-input"
                    value={countryCode}
                    onChange={(e) => {
                      let v = e.target.value;
                      if (!v.startsWith('+')) v = `+${v.replace(/\D/g, '')}`;
                      setCountryCode(v.slice(0, 4));
                    }}
                    className="bg-white/[0.03] border-white/15 text-white h-12 rounded-xl w-20 text-center"
                  />
                  <Input
                    data-testid="otp-phone-input"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                    placeholder="9876543210"
                    inputMode="numeric"
                    className="bg-white/[0.03] border-white/15 text-white h-12 rounded-xl flex-1"
                  />
                </div>
                <p className="text-[10px] text-white/40 mt-1.5">
                  We&apos;ll text a 6-digit code to <span className="text-white/70">{fullPhone}</span>
                </p>
              </div>

              <Button
                type="submit"
                disabled={loading}
                data-testid="otp-send-btn"
                className="w-full h-14 bg-[#FF007F] hover:bg-[#E5006F] text-white font-black rounded-2xl text-sm tracking-[0.3em] uppercase shadow-[0_8px_30px_rgba(255,0,127,0.4)]"
              >
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Send SMS Code'}
              </Button>
            </motion.form>
          )}

          {step === 'code' && (
            <motion.div
              key="code-step"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-5"
            >
              <div className="flex items-center gap-3 mb-1">
                <div className="w-12 h-12 rounded-2xl bg-[#FF007F]/15 flex items-center justify-center ring-1 ring-[#FF007F]/30">
                  <ShieldCheck className="w-6 h-6 text-[#FF007F]" />
                </div>
                <div>
                  <h2 className="text-xl text-white font-['Outfit'] font-bold leading-tight">
                    Code sent
                  </h2>
                  <p className="text-xs text-white/55">
                    Check SMS on <span className="text-white/80">{fullPhone}</span>
                  </p>
                </div>
              </div>

              <div className="flex gap-2 justify-between" data-testid="otp-code-row">
                {otp.map((d, i) => (
                  <input
                    key={i}
                    ref={(el) => (otpRefs.current[i] = el)}
                    value={d}
                    inputMode="numeric"
                    maxLength={1}
                    onChange={(e) => handleOtpChange(i, e.target.value)}
                    onKeyDown={(e) => handleOtpKeyDown(i, e)}
                    onPaste={i === 0 ? handleOtpPaste : undefined}
                    data-testid={`otp-digit-${i}`}
                    className="w-12 h-14 bg-white/[0.04] border border-white/15 rounded-xl text-center text-white text-2xl font-bold focus:border-[#FF007F] focus:ring-2 focus:ring-[#FF007F]/30 outline-none"
                  />
                ))}
              </div>

              <Button
                onClick={() => handleVerify()}
                disabled={loading || otp.some((d) => !d)}
                data-testid="otp-verify-btn"
                className="w-full h-14 bg-gradient-to-r from-[#FF007F] to-[#FF3D00] hover:opacity-95 text-white font-black rounded-2xl text-sm tracking-[0.3em] uppercase"
              >
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Verify & Continue'}
              </Button>

              <button
                type="button"
                onClick={resendCooldown === 0 ? handleSendOtp : undefined}
                disabled={resendCooldown > 0}
                data-testid="otp-resend-btn"
                className="w-full text-center text-xs text-white/60 hover:text-white py-2 disabled:opacity-40"
              >
                {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend code'}
              </button>
            </motion.div>
          )}
        </AnimatePresence>
    </>
  );

  if (inline) {
    return (
      <motion.div
        data-testid="guest-otp-inline"
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
        className="w-full bg-white/[0.04] border border-white/12 rounded-3xl p-5 backdrop-blur-xl"
      >
        {body}
      </motion.div>
    );
  }

  return (
    <motion.div
      data-testid="guest-otp-modal"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-6"
    >
      <motion.div
        initial={{ y: 40, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ type: 'spring', damping: 24, stiffness: 240 }}
        className="w-full sm:max-w-md bg-[#0a0a0a] border border-white/12 rounded-t-[32px] sm:rounded-[28px] p-6 pt-7 shadow-2xl"
      >
        {body}
      </motion.div>
    </motion.div>
  );
};

export default GuestOtpModal;
